export const LOREM_FIRST = "Lorem ipsum dolor sit amet, consectetur adipiscing elit.";
const WORDS = ("lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi aliquip ex ea commodo consequat duis aute irure in reprehenderit voluptate velit esse cillum eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt culpa qui officia deserunt mollit anim id est laborum").split(" ");
/** Latin sentences from the standard passage and Cicero's De finibus (1.10.32), public domain. */
export const CLASSIC_SENTENCES = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
  "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
  "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.",
  "Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
  "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.",
  "Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.",
  "Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem.",
  "Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur.",
  "Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur.",
];
const CLASSIC_WORDS = CLASSIC_SENTENCES.join(" ").toLowerCase().replace(/[.,]/g, "").split(" ");
export type ParagraphLength = "short" | "medium" | "long";
export const SENTENCES_PER_PARAGRAPH: Record<ParagraphLength, [number, number]> = { short: [2, 3], medium: [4, 6], long: [7, 10] };
export const MAX_COUNT = 1000;

export function clampCount(raw: string): { n: number; note: string | null } {
  const v = Math.floor(Number(raw));
  if (!Number.isFinite(v) || v < 1) return { n: 1, note: "Count must be at least 1 — using 1." };
  if (v > MAX_COUNT) return { n: MAX_COUNT, note: `Count is limited to ${MAX_COUNT} — using ${MAX_COUNT}.` };
  return { n: v, note: null };
}

export function makeRng(seed = Date.now()) {
  let s = seed >>> 0 || 1;
  return () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
}

function sentence(rng: () => number, minW = 4, maxW = 12): string {
  const len = minW + Math.floor(rng() * (maxW - minW + 1));
  const w = Array.from({ length: len }, () => WORDS[Math.floor(rng() * WORDS.length)]);
  if (len > 7 && rng() < 0.5) { const i = 3 + Math.floor(rng() * (len - 5)); w[i] += ","; }
  const t = w.join(" ");
  return t[0].toUpperCase() + t.slice(1) + ".";
}

export interface LoremOpts {
  count: number; type: "paragraphs" | "sentences" | "words"; startWithLorem: boolean; html: boolean; rng?: () => number;
  /** Sentences per paragraph. Default "medium". */
  length?: ParagraphLength;
  /** "classic" uses real Latin sentences in order instead of random words. Default "random". */
  vocab?: "random" | "classic";
}

export function generateLorem({ count, type, startWithLorem, html, rng = makeRng(), length = "medium", vocab = "random" }: LoremOpts): string {
  const classic = vocab === "classic";
  const cap = (t: string) => t[0].toUpperCase() + t.slice(1);
  if (type === "words") {
    const w: string[] = [];
    const lead = LOREM_FIRST.replace(/[.,]/g, "").toLowerCase().split(" ");
    const pool = classic ? CLASSIC_WORDS : WORDS;
    const offset = classic && !startWithLorem ? Math.floor(rng() * pool.length) : 0;
    for (let i = 0; i < count; i++) w.push(startWithLorem && i < lead.length ? lead[i] : classic ? pool[(offset + i) % pool.length] : pool[Math.floor(rng() * pool.length)]);
    return cap(w.join(" ")) + ".";
  }
  // Classic text is read in order from a start point so it stays real Latin prose.
  let cursor = classic && !startWithLorem ? Math.floor(rng() * CLASSIC_SENTENCES.length) : 0;
  const nextSentence = () => (classic ? CLASSIC_SENTENCES[cursor++ % CLASSIC_SENTENCES.length] : sentence(rng));
  if (type === "sentences") {
    const s = Array.from({ length: count }, nextSentence);
    if (startWithLorem) s[0] = LOREM_FIRST;
    return s.join(" ");
  }
  const [lo, hi] = SENTENCES_PER_PARAGRAPH[length];
  const paras = Array.from({ length: count }, (_, i) => {
    const n = lo + Math.floor(rng() * (hi - lo + 1));
    const s = Array.from({ length: n }, nextSentence);
    if (i === 0 && startWithLorem) s[0] = LOREM_FIRST;
    return s.join(" ");
  });
  return html ? paras.map(p => `<p>${p}</p>`).join("\n") : paras.join("\n\n");
}

export function countStats(text: string) {
  const plain = text.replace(/<\/?p>/g, "");
  const words = plain.trim() ? plain.trim().split(/\s+/).length : 0;
  return { words, chars: plain.length };
}
